import React, { useState, useMemo, useEffect } from 'react';
import UserTable from './components/UserTable';
import UserForm from './components/UserForm';
import PasswordModal from './components/PasswordModal';
import DmarcDashboard from './components/DmarcDashboard';
import { user_api } from "../services/auth";
import ErrorModal from './components/ErrorModal';
import { getCurrentUser, logout } from '../utils/auth';
import ReportPanel from './components/ReportPanel';
import ManageDomainsModal from './components/ManageDomainsModal';
import SessionExpiredModal from './components/SessionExpiredModal';
import UnauthorizedDomainModal from './components/UnauthorizedDomainModal';
import { fetchUserDomainsFromAPI } from '../services/domainService';

// Base que será /api/dmarc en producción
const API_BASE = process.env.REACT_APP_API_BASE || "/api/dmarc";
// Para el PDF (endpoint /reports/dmarc.pdf) necesitamos la raíz sin /dmarc
const API_ROOT = (process.env.REACT_APP_API_BASE || "/api/dmarc").replace(
  /\/dmarc\/?$/,
  "",
);

const DmarcOne = () => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [generatedPwd, setGeneratedPwd] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'nombre', direction: 'asc' });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [isSessionExpiredOpen, setIsSessionExpiredOpen] = useState(false);
  const [isUnauthorizedDomainOpen, setIsUnauthorizedDomainOpen] = useState(false);
  const [unauthorizedDomain, setUnauthorizedDomain] = useState('');


  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });

  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('dmarc-theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const root = window.document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem('dmarc-theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('dmarc-theme', 'light');
    }
    setLoggedInUser(getCurrentUser());
      const handleSessionExpiredEvent = () => {
      setIsSessionExpiredOpen(true);
    };

    window.addEventListener('session-expired', handleSessionExpiredEvent);
    window.addEventListener('unauthorized-domain', handleUnauthorizedDomainEvent);
    return () => {
      window.removeEventListener('session-expired', handleSessionExpiredEvent);
      window.removeEventListener('unauthorized-domain', handleUnauthorizedDomainEvent);
  
    };
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);
  const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);

  const fetchUsers = async () => {
    const userList = await user_api.get("/user-list");
    if (userList.status === 200) {
      setUsers(userList.data.detail);
    }
  };

  const generatePassword = () => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
    let retVal = "";
    for (let i = 0, n = charset.length; i < 12; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * n));
    }
    return retVal;
  };

  const especialistas = useMemo(() =>
    users.filter(u => u.rol === 'especialista'),
  [users]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedUsers = useMemo(() => {
    let result = [...users];
    const term = searchTerm.toLowerCase();
    if (term && users) {
      result = result.filter(u =>
        u.nombre?.toLowerCase().includes(term) ||
        u.correo?.toLowerCase().includes(term) ||
        u.numcliente?.toLowerCase().includes(term)
      );
    }
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (sortConfig.key === 'vence_en') {
        if (!valA) valA = '9999-12-31';
        if (!valB) valB = '9999-12-31';
      }
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [users, searchTerm, sortConfig]);

  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    const expiringSoonCount = users.filter(u => {
      if (u.rol !== 'cliente' || !u.vence_en) return false;
      const expiry = new Date(u.vence_en);
      return expiry >= today && expiry <= thirtyDaysFromNow;
    }).length;
    return {
      total: users.length,
      especialistas: especialistas.length,
      clientesActivos: users.filter(u => u.activo && u.rol === 'cliente').length,
      vencenPronto: expiringSoonCount
    };
  }, [users, especialistas]);

  const handleCreateUser = () => {
    setCurrentUser(null);
    setIsCreating(true);

    setIsEditing(false);
  };

  const handleEditUser = (user) => {
    setCurrentUser(user);
    setIsEditing(true);
    setIsCreating(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('¿Está seguro de que desea eliminar este usuario? Esta acción no se puede deshacer.')) {
      setUsers(prev => prev.filter(u => u.id !== userId));
      if (currentUser?.id === userId) {
        setIsEditing(false);
        setCurrentUser(null);
      }
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const result = await user_api.post("/toggle-active", { id: userId });
      if (!result.data.success) {
        setErrorModal({
            isOpen: true,
            message: "Error al modificar usuario. " + result.data.error
        });
      }
      setUsers(prev => prev.map(u => u.id === userId && u.rol !== 'especialista' ? { ...u, activo: u.activo === '1' ? '0' : '1' } : u));
    } catch (e) {
      setErrorModal({ isOpen: true, message: "Error al modificar el usuario. " + e});
    }
  };

  const handleSubmit = async (data) => {
    if (isCreating) {
      const newId = Math.max(0, ...users.map(u => u.id)) + 1;
      const newUser= { ...data, id: newId };
      const pwd = generatePassword();
      setGeneratedPwd(pwd);
      const result = await user_api.post(
        "/create-user",
        { name: newUser.nombre,
          email: newUser.correo,
          password: pwd,
          role: newUser.rol,
          activo: newUser.activo,
          vence_en: newUser.vence_en,
          num_cliente: newUser.numcliente,
          id_especialista: newUser.id_ejecutivo
        }
      );

      if (result.status === 200) {
        if (result.data.success) {
          setShowPwdModal(true);
          setUsers(prev => [...prev, newUser]);
        } else {
          setErrorModal({
              isOpen: true,
              message: 'Error al crear usuario. ' + result.data.msg
          });
        }
        if (data.dominios.length > 0) {
          const resultsDominios = await Promise.all(data.dominios.map(async (d) => {
            const result = await user_api.post(
              "/asignar-dominio",
              {
                id: newUser.id,
                domain: d
              }
            );
            return result.data;
          }));
          if (!resultsDominios.every((r) => r.success)) {
            const errorMsg = 
              resultsDominios.filter((r) => !r.success)
              .reduce((acc, r) => acc + r.msg, 'Error al asginar dominios a usuario nuevo. ');
            setErrorModal({
              isOpen: true,
              message: 'Error al crear usuario. ' + result.data.msg
            });
          }
        }
      }
      setIsCreating(false);
    }
    else if (isEditing) {
      const newUser = data ;
      const result = await user_api.post(
        "/update-user",
        { name: newUser.nombre,
          email: newUser.correo,
          role: newUser.rol,
          activo: newUser.activo,
          vence_en: newUser.vence_en,
          num_cliente: newUser.numcliente,
          id_especialista: newUser.id_ejecutivo
        }
      );

      if (result.status === 200) {
        if (result.data.success) {
          setUsers(prev => prev.map( p => p.id === newUser.id ? newUser : p));
        } else {
          setErrorModal({
              isOpen: true,
              message: 'Error al editar usuario. ' + result.data.msg
          });
        }
      }
      setIsEditing(false);
    }
  };

  const getEspecialistaName = (id) => {
    if (!id) return 'Sin asignar';
    const esp = especialistas.find(e => e.id === id);
    return esp ? esp.nombre : 'Desconocido';
  };
  
  // Estados para gestión de dominios
  const [showDomainsModal, setShowDomainsModal] = useState(false);
  const [userForDomains, setUserForDomains] = useState(null);

  const handleManageDomains = (user) => {
    setUserForDomains(user);
    setShowDomainsModal(true);
  };

  const handleSaveDomains = (userId, domains  ) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, dominios: domains } : u));
    if (currentUser && currentUser.id === userId) {
      setCurrentUser(prev => prev ? { ...prev, dominios: domains } : null);
    }
    if (userForDomains && userForDomains.id === userId) {
      setUserForDomains(prev => prev ? { ...prev, dominios: domains } : null);
    }
  };

  // Manejador al hacer clic en "Ir a Iniciar Sesión"
  const handleRedirectToLogin = () => {
    // Limpia el caché / tokens de sesión
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    setIsSessionExpiredOpen(false);

    // Redirige al login
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    } else {
      window.location.reload();
    }
  };

  const nextId = async () => {
    const result = await user_api.get("/siguiente-id");

    if (result.data.success) 
      return result.data.id;

    setErrorModal({isOpen: true, message: result.data.msg});
  };
  
  const handleCloneUser = async (templateUser) => {
    let initialDominios = templateUser.dominios ? [...templateUser.dominios] : [];
    let newId = await nextId() ?? 0;

    if (newId === 0) {
      return;
    }

    if (templateUser.id > 0) {
      const apiDomains = await fetchUserDomainsFromAPI(templateUser.id);
      if (apiDomains && apiDomains.length > 0) {
        initialDominios = Array.from(new Set([...initialDominios, ...apiDomains]));
      }
    }

    setCurrentUser({
      id: 0,
      nombre: `${templateUser.nombre} (Copia)`,
      correo: '',
      rol: templateUser.rol,
      activo: true,
      vence_en: templateUser.vence_en || new Date().toISOString().split('T')[0],
      numcliente: templateUser.numcliente ? `DM-${newId}-${new Date().toISOString().split('T')[0]}` : '',
      id_ejecutivo: templateUser.id_ejecutivo,
      dominios: initialDominios,
    });
    setIsCreating(true);
    setIsEditing(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const handleUnauthorizedDomainEvent = (e) => {
    const detail = (e)?.detail;
    if (detail && typeof detail === 'object' && detail.domain) {
      setUnauthorizedDomain(detail.domain);
    } else {
      setUnauthorizedDomain('');
    }
    setIsUnauthorizedDomainOpen(true);
  };


  return (
    <div className="min-h-screen flex font-sans transition-colors duration-500 bg-slate-50 dark:bg-slate-950">
      {/* Sidebar Colapsable */}
      <aside className={`bg-cyan-900 text-white sticky top-0 h-screen p-4 flex flex-col shadow-2xl transition-all duration-300 z-30 flex-shrink-0 ${isSidebarCollapsed ? 'w-16' : 'w-52'}`}>
        <div className={`flex items-center mb-10 overflow-hidden ${isSidebarCollapsed ? 'justify-center' : 'space-x-2'}`}>
          <div className="w-9 h-9 bg-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-900/50">
            <i className="fas fa-shield-halved text-white text-lg"></i>
          </div>
          {!isSidebarCollapsed && <span className="text-xl font-black tracking-tighter whitespace-nowrap">DMARC<span className="text-cyan-400">ONE</span></span>}
        </div>

        <nav className="flex-1 space-y-2">
          <button
            onClick={() => setCurrentView('dashboard')}
            title="Dashboard"
            className={`w-full flex items-center p-3 rounded-xl transition-all text-sm ${isSidebarCollapsed ? 'justify-center' : 'space-x-3'} ${
              currentView === 'dashboard'
                ? 'bg-cyan-800/50 text-cyan-400 font-bold border-r-4 border-cyan-400'
                : 'text-cyan-200 hover:bg-cyan-800'
            }`}
          >
            <i className="fas fa-chart-line w-5 text-center text-lg"></i>
            { !isSidebarCollapsed &&
              <span className="truncate font-semibold">
                Dashboard
              </span>
            }
          </button>
          { false && <button // FIXME: Borrar?
            onClick={() => setCurrentView('config')}
            title="Ajustes"
            className={`w-full flex items-center p-3 rounded-xl transition-all text-sm ${isSidebarCollapsed ? 'justify-center' : 'space-x-3'} ${
              currentView === 'config'
                ? 'bg-cyan-800/50 text-cyan-400 font-bold border-r-4 border-cyan-400'
                : 'text-cyan-200 hover:bg-cyan-800'
            }`}
          >
            <i className="fas fa-cog w-5 text-center text-lg"></i>
            {!isSidebarCollapsed && <span className="truncate font-semibold">Ajustes</span>}
          </button>
          }
          {
          loggedInUser?.rol === 'especialista'
          &&
          <button
            onClick={() => setCurrentView('reportes')}
            className={
                `w-full flex items-center p-3 rounded-xl transition-all text-sm
                 ${ isSidebarCollapsed ? 'justify-center' : 'space-x-3' }
                 ${ currentView === 'reportes'
                    ? 'bg-cyan-800/50 text-cyan-400 font-bold border-r-4 border-cyan-400'
                    : 'text-cyan-200 hover:bg-cyan-800'
                 }`
            }
          >
            <i className='fas fa-file-archive ws-5 text-center text-lg'></i>
            { !isSidebarCollapsed &&
              <span className='truncate font-semibold'>
                Reportes
              </span>
            }
          </button>
          }
          { (loggedInUser?.rol === 'especialista') &&
          <button
            onClick={() => { setCurrentView('users'); fetchUsers(); }}
            title="Usuarios"
            className={`w-full flex items-center p-3 rounded-xl transition-all text-sm ${isSidebarCollapsed ? 'justify-center' : 'space-x-3'} ${
              currentView === 'users'
                ? 'bg-cyan-800/50 text-cyan-400 font-bold border-r-4 border-cyan-400'
                : 'text-cyan-200 hover:bg-cyan-800'
            }`}
          >
            <i className="fas fa-users-cog w-5 text-center text-lg"></i>
            {!isSidebarCollapsed && <span className="truncate font-semibold">Usuarios</span>}
          </button>
          }
        </nav>

        <div className="mt-auto pt-4 border-t border-cyan-800">
          <div className={`flex items-center p-1 rounded-xl transition-colors ${isSidebarCollapsed ? 'flex-col space-y-2' : 'justify-between space-x-2'}`}>
            {!isSidebarCollapsed && (
              <div className="flex items-center space-x-2 overflow-hidden flex-1">
                <div className="w-7 h-7 rounded-full bg-cyan-400 flex items-center justify-center font-bold text-cyan-900 text-xs flex-shrink-0 shadow-md">
                  {loggedInUser?.nombre.split(' ').reduce((str, x) => str + x[0], '')}
                </div>
                <div className="flex flex-col text-left truncate">
                  <span className="text-xs font-bold text-white leading-none truncate">{loggedInUser?.nombre}</span>
                  <span className="text-[10px] text-cyan-400 uppercase font-medium">{loggedInUser?.rol}</span>
                </div>
              </div>
            )}
            <button
              onClick={toggleTheme}
              className={`flex items-center justify-center rounded-lg bg-cyan-800 hover:bg-cyan-700 text-cyan-200 hover:text-white transition-all duration-300 flex-shrink-0 ${isSidebarCollapsed ? 'w-8 h-8' : 'w-8 h-8 md:w-9 md:h-9'}`}
              aria-label="Alternar tema"
              title={isDark ? "Modo claro" : "Modo oscuro"}
            >
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'} text-sm`}></i>
            </button>
          </div>
          <button
              onClick={(_) => {logout(); window.location.replace('/login');}}
              className={`flex items-center justify-center rounded-lg bg-cyan-800 hover:bg-cyan-700 text-cyan-200 hover:text-white transition-all duration-300 ${isSidebarCollapsed ? 'w-full h-8' : 'w-full h-8'}`}>
            <i className='fas fa-sign-out-alt'></i>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 min-w-0 transition-colors duration-500 overflow-x-hidden flex flex-col">
        <header className="sticky top-0 z-20 bg-slate-50/90 dark:bg-slate-950/90 backdrop-blur-md border-b border-gray-100 dark:border-slate-800 p-3 md:p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400 transition-colors"
              title={isSidebarCollapsed ? "Expandir menú" : "Contraer menú"}
            >
              <i className={`fas ${isSidebarCollapsed ? 'fa-indent' : 'fa-outdent'} text-lg`}></i>
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-black text-gray-800 dark:text-slate-100 leading-tight">
                {
                    currentView === 'users'
                    ? 'Gestión de Usuarios'
                    : currentView === 'reportes'
                    ? 'Reportes'
                    : currentView === 'dashboard'
                    ? 'Panel DMARC'
                    : 'Ajustes'
                }
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {currentView === 'users' && (
              <button
                onClick={handleCreateUser}
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-xl font-bold flex items-center space-x-2 transition shadow-lg shadow-cyan-100 dark:shadow-none hover:scale-105 active:scale-95 text-sm"
              >
                <i className="fas fa-plus"></i>
                <span className="hidden sm:inline">Nuevo Usuario</span>
              </button>
            )}
          </div>
        </header>

        <div className="p-4 md:p-6 lg:p-8 flex-1">
          {currentView === 'users' ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Total', value: stats.total, icon: 'fa-users', color: 'cyan' },
                  { label: 'Especialistas', value: stats.especialistas, icon: 'fa-user-tie', color: 'indigo' },
                  { label: 'Activos', value: stats.clientesActivos, icon: 'fa-user-check', color: 'emerald' },
                  { label: 'Vencen Pronto', value: stats.vencenPronto, icon: 'fa-clock', color: 'orange' },
                ].map((stat, i) => (
                  <div key={i} className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm flex items-center space-x-4 transition-all duration-500">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl shadow-sm ${
                      stat.color === 'orange' ? 'bg-orange-100 text-orange-600 dark:bg-orange-400/20 dark:text-orange-400' :
                      stat.color === 'emerald' ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-400/20 dark:text-emerald-400' :
                      stat.color === 'indigo' ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-400/20 dark:text-indigo-400' :
                      'bg-cyan-100 text-cyan-600 dark:bg-cyan-400/20 dark:text-cyan-400'
                    }`}>
                      <i className={`fas ${stat.icon}`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest truncate">{stat.label}</p>
                      <p className="text-2xl font-black text-gray-800 dark:text-slate-100">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <section className="space-y-6">
                <div className="bg-white dark:bg-slate-900 p-1.5 rounded-xl shadow-sm border border-gray-100 dark:border-slate-800 flex items-center px-4 max-w-md focus-within:ring-2 focus-within:ring-cyan-500/20">
                  <i className="fas fa-search text-gray-400 dark:text-slate-500 mr-3 text-sm"></i>
                  <input
                    type="text"
                    placeholder="Buscar por nombre, correo o ID..."
                    className="w-full py-2 outline-none text-sm text-gray-600 dark:text-slate-200 bg-transparent placeholder-gray-400 dark:placeholder-slate-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {(isCreating || isEditing) && (
                  <div className="mb-8 animate-in fade-in slide-in-from-top-4 duration-300">
                    <UserForm
                      initialData={currentUser}
                      allUsers={users}
                      especialistas={especialistas}
                      onCancel={() => { setIsCreating(false); setIsEditing(false); }}
                      onSubmit={handleSubmit}
                      mode={isEditing ? 'editing' : 'creating'}
                      onManageDomains={handleManageDomains}
                      user={userForDomains}
                    />
                  </div>
                )}

                <UserTable
                  users={filteredAndSortedUsers}
                  onEdit={handleEditUser}
                  onDelete={handleDeleteUser}
                  onToggleStatus={handleToggleStatus}
                  getEspecialistaName={getEspecialistaName}
                  onCloneUser={handleCloneUser}
                  onSort={handleSort}
                  sortConfig={sortConfig}
                  onManageDomains={handleManageDomains}
                />
              </section>
            </div>
          ) : currentView === 'dashboard' ? (
              <DmarcDashboard />
          ) : currentView === 'reportes' ? (
              <ReportPanel />
          ) : (
            <div className="bg-white dark:bg-slate-900 p-12 rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm text-center">
              <i className="fas fa-tools text-6xl text-cyan-200 dark:text-slate-700 mb-6 block"></i>
              <h2 className="text-xl font-bold text-gray-800 dark:text-slate-100">Configuración Avanzada</h2>
              <p className="text-sm text-gray-500 dark:text-slate-400 max-w-sm mx-auto">Esta sección permite ajustar parámetros globales del sistema DMARC y se encuentra actualmente bajo mantenimiento.</p>
            </div>
          )}
        </div>
      </main>

      <PasswordModal
        isOpen={showPwdModal}
        userName={newUserName}
        password={generatedPwd}
        onClose={() => setShowPwdModal(false)}
      />
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
      />
      <ManageDomainsModal
        isOpen={showDomainsModal}
        user={userForDomains}
        allUsers={users}
        onClose={() => {
          setShowDomainsModal(false);
          setUserForDomains(null);
        }}
        onSave={handleSaveDomains}
      />
      <SessionExpiredModal
        isOpen={isSessionExpiredOpen}
        onConfirm={handleRedirectToLogin}
      />

      <UnauthorizedDomainModal
        isOpen={isUnauthorizedDomainOpen}
        domain={unauthorizedDomain}
        onClose={() => setIsUnauthorizedDomainOpen(false)}
      />
    </div>
  );
};

export default DmarcOne;
